import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "src/components/Toast";

export interface SafeApiCallOptionsTypes {
  toastSuccessMsg?: string;
  toastErrorMsg?: string;
  useServerErrorAsToast?: boolean;
  retryFn?: () => void;
  suppressErrorRedirect?: boolean;
  suppressDuplicateToasts?: boolean;
}

export const useSafeApiCall = () => {
  const lastErrorToastKey = useRef("");
  const { setPendingToast } = useToast();
  const navigate = useNavigate();

  return function safeApiCall<T>(
    apiCall: () => Promise<T>,
    options?: SafeApiCallOptionsTypes | null,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const {
        toastSuccessMsg,
        toastErrorMsg,
        retryFn,
        suppressErrorRedirect,
        suppressDuplicateToasts,
        useServerErrorAsToast,
      } = options || {};

      apiCall()
        .then((data) => {
          if (toastSuccessMsg) {
            setPendingToast(toastSuccessMsg, "success");
          }
          resolve(data);
        })
        .catch((err: { status: number; message: string }) => {
          const errorKey = `${toastErrorMsg ?? ""}_${err?.status}`;
          if (
            !suppressDuplicateToasts ||
            errorKey !== lastErrorToastKey.current ||
            retryFn
          ) {
            lastErrorToastKey.current = errorKey;

            if (toastErrorMsg) {
              setPendingToast(toastErrorMsg, "error", retryFn);
            }

            if (!toastErrorMsg && useServerErrorAsToast && err?.message) {
              setPendingToast(
                err.message.charAt(0).toUpperCase() + err.message.slice(1),
                "error",
                retryFn,
              );
            }
          }

          if (
            !suppressErrorRedirect &&
            (err?.status === 403 || err?.status === 404)
          ) {
            void navigate("/error", {
              state: { status: err.status },
              replace: true,
            });
          }

          reject(
            err instanceof Error
              ? err
              : Object.assign(new Error(err?.message), err),
          );
        });
    });
  };
};
